import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Logic Trail — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/logic-trail.md §FTUE
 *   E1 : TMT-A 5 items. Connecter 1-2-3-4-5.
 *   E2 : TMT-A 7 items. Plus dense.
 *   E3 : TMT-B 6 items. Alternance digit/lettre 1-A-2-B-3-C.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
const STAGE = 240;
const NODE_R = 14;
function gridNodes(labels) {
    // Disposition deterministe sur grille 3x3 (jusqu'a 9 items).
    // Coordonnees normalisees [0..1].
    const positions = [
        [0.2, 0.25],
        [0.75, 0.30],
        [0.30, 0.55],
        [0.65, 0.70],
        [0.50, 0.85],
        [0.85, 0.55],
        [0.15, 0.80],
        [0.50, 0.40],
        [0.80, 0.85],
    ];
    return labels.map((label, i) => ({
        id: `n${i}`,
        label,
        x: positions[i % positions.length][0],
        y: positions[i % positions.length][1],
        seqIdx: i,
    }));
}
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'logic_trail.demo.step1_title',
        hintKey: 'logic_trail.demo.step1_hint',
        nodes: gridNodes(['1', '2', '3', '4', '5']),
    },
    {
        idx: 2,
        titleKey: 'logic_trail.demo.step2_title',
        hintKey: 'logic_trail.demo.step2_hint',
        nodes: gridNodes(['1', '2', '3', '4', '5', '6', '7']),
    },
    {
        idx: 3,
        titleKey: 'logic_trail.demo.step3_title',
        hintKey: 'logic_trail.demo.step3_hint',
        nodes: gridNodes(['1', 'A', '2', 'B', '3', 'C']),
    },
];
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [expectedIdx, setExpectedIdx] = useState(0);
    const [errors, setErrors] = useState(0);
    const [done, setDone] = useState(false);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const segments = useMemo(() => {
        const segs = [];
        const visited = step.nodes.slice(0, expectedIdx);
        for (let i = 0; i < visited.length - 1; i += 1) {
            const a = visited[i];
            const b = visited[i + 1];
            segs.push({
                x1: a.x * STAGE,
                y1: a.y * STAGE,
                x2: b.x * STAGE,
                y2: b.y * STAGE,
            });
        }
        return segs;
    }, [step, expectedIdx]);
    const handleTap = (id) => {
        if (done)
            return;
        const expected = step.nodes[expectedIdx];
        if (!expected)
            return;
        if (id === expected.id) {
            const next = expectedIdx + 1;
            setExpectedIdx(next);
            if (next >= step.nodes.length) {
                setDone(true);
            }
        }
        else {
            setErrors((e) => e + 1);
        }
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setExpectedIdx(0);
        setErrors(0);
        setDone(false);
    };
    const expectedLabel = step.nodes[expectedIdx]?.label ?? '✓';
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('logic_trail.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "flex w-full max-w-[320px] items-center justify-between text-[11px] text-cyan-text-secondary", children: [_jsxs("span", { children: [t('logic_trail.hud.expected_label'), " :", ' ', _jsx("span", { className: "font-mono text-cyan-network", children: expectedLabel })] }), _jsxs("span", { children: [t('logic_trail.hud.errors'), " :", ' ', _jsx("span", { className: "font-mono", children: errors })] })] }), _jsxs("div", { className: "relative", style: { width: STAGE, height: STAGE, background: '#1a1a1a' }, children: [_jsx("svg", { width: STAGE, height: STAGE, className: "absolute inset-0", "aria-hidden": true, children: segments.map((s, i) => (_jsx("line", { x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2, stroke: "#7d9b6a", strokeWidth: 3, strokeLinecap: "round" }, i))) }), step.nodes.map((node) => {
                        const cx = node.x * STAGE;
                        const cy = node.y * STAGE;
                        const isVisited = node.seqIdx < expectedIdx;
                        const isNext = !done && node.seqIdx === expectedIdx;
                        let bg = '#2b2b2b';
                        let border = '#3d3d3d';
                        let textCls = 'text-cyan-surface';
                        if (isVisited) {
                            bg = '#3a4d2f';
                            border = '#7d9b6a';
                        }
                        else if (isNext) {
                            border = '#bcd8a3';
                            textCls = 'text-cyan-surface';
                        }
                        return (_jsx("button", { type: "button", onClick: () => handleTap(node.id), "aria-label": node.label, className: [
                                'absolute flex items-center justify-center rounded-full border-2 font-display text-[13px] transition duration-feedback ease-soft-wind active:scale-95',
                                textCls,
                                isNext ? 'animate-pulse' : '',
                            ].join(' '), style: {
                                width: NODE_R * 2,
                                height: NODE_R * 2,
                                left: cx - NODE_R,
                                top: cy - NODE_R,
                                background: bg,
                                borderColor: border,
                            }, children: node.label }, node.id));
                    })] }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [done && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map